// filepath: apps/web/src/auth/oidcConfig.ts
//
// Pure OIDC config builder (FR-UI-CORE-2 AC-2, AC-3, AC-4).
//
// This module is a PURE FUNCTION — no React, no I/O, no side effects.
// It takes the resolved tenant config + the SPA's origin and returns
// the UserManagerSettings object that react-oidc-context /
// oidc-client-ts consumes.
//
// Why pure: the config is the audit fixture for INV-token-storage,
// INV-2, and the AC-3 static guard. Pulling it out of a React
// component makes it (a) testable in isolation, (b) greppable for
// forbidden patterns (client_secret, implicit flow, etc.), and
// (c) deterministic across renders.

import type { UserManagerSettings } from "oidc-client-ts";
import type { TenantConfig } from "./types";

/**
 * The single client_id used by the SPA. All tenants share this
 * client_id — the realm URL (per-tenant) is what scopes the auth
 * boundary. Constants are defined here so the static-guard test can
 * grep for them.
 */
export const SPA_CLIENT_ID = "ascoa-web";

/**
 * The OIDC scope requested. RFC 8414 minimum + identity claims.
 */
export const SPA_SCOPE = "openid profile email";

/**
 * Redirect URI path component. The full URL is constructed as
 * `${origin}${SPA_REDIRECT_PATH}` to bind the redirect to the
 * current tenant's subdomain (R4, INV-6).
 */
export const SPA_REDIRECT_PATH = "/oidc-callback";

/**
 * The default isolation / hardening configuration. The function
 * takes no UserManagerSettings — the SPA does NOT allow per-tenant
 * overrides of the auth flow (AC-3).
 */
export interface OidcConfigInput {
  /** Tenant config from /api/tenant-config (the realm URL). */
  tenantConfig: TenantConfig;
  /** Window origin. Used to bind the redirect to the current tenant. */
  origin: string;
}

/**
 * Build the UserManagerSettings for the resolved tenant.
 *
 * Hardening invariants (AC-3, AC-4, AC-6, INV-token-storage):
 *
 *  - response_type: 'code' (Authorization Code flow, RFC 6749 §4.1).
 *  - disablePKCE: false (PKCE REQUIRED, RFC 7636).
 *  - automaticSilentRenew: true (proactive refresh before expiry).
 *  - includeIdTokenInSilentRenew: true (refresh-token grant includes id_token).
 *  - client_id: SPA_CLIENT_ID (public client per RFC 8252).
 *  - client_secret: NOT SET (public client — no shared secret).
 *  - userStore: undefined (default = in-memory; NEVER WebStorageStateStore).
 *  - redirect_uri: origin + SPA_REDIRECT_PATH (bound to current tenant).
 *  - post_logout_redirect_uri: origin (back to the tenant's home).
 *  - scope: SPA_SCOPE (openid + profile + email).
 *
 * Any future change that breaks one of these invariants MUST be
 * reviewed by SecurityReviewer (see ARCHITECTURE_WEB.md §7).
 */
export function buildOidcConfig(input: OidcConfigInput): UserManagerSettings {
  const { tenantConfig, origin } = input;

  // INV-6 / R4: bind the redirect URI to the current origin. The
  // SPA NEVER accepts a `redirect_uri` from a query parameter.
  const redirectUri = `${origin}${SPA_REDIRECT_PATH}`;
  const postLogoutRedirectUri = origin;

  // Public client (RFC 8252). NO client_secret. The SPA's static-guard
  // test asserts no `client_secret` string in the production bundle.
  const settings: UserManagerSettings = {
    authority: tenantConfig.realm_url,
    client_id: SPA_CLIENT_ID,
    response_type: "code",
    scope: SPA_SCOPE,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    automaticSilentRenew: true,
    includeIdTokenInSilentRenew: true,
    // loadUserInfo: defaults to true — fetch the userinfo endpoint
    // to populate email/roles claims. The SPA needs email and roles
    // for the session-aware UI.
    loadUserInfo: true,
    // silentRequestTimeoutInSeconds: 10s — refresh failure within
    // this window triggers the AC-4 redirect-to-login path.
    silentRequestTimeoutInSeconds: 10,
    // filterProtocolClaims: true — strip the OIDC protocol claims
    // (nbf, jti, etc.) from the user object exposed to the app.
    filterProtocolClaims: true,
    // revokeTokenAdditionalContentTypes: ['none'] — default behaviour
    // requires explicit opt-in for revocation calls.
    // extraQueryParams: keep empty — the realm URL is the only
    // per-tenant hint; no `tenant_id` parameter leaks via the
    // authorize URL.
  };

  // INV-token-storage: ensure userStore is NOT a WebStorageStateStore.
  // The library default is in-memory when userStore is undefined.
  // We assert this in the test (oidcConfig.test.ts) and the static-guard
  // test verifies no `WebStorageStateStore` is imported in src/auth/.
  return settings;
}
